import React, { useState, useEffect } from 'react';
import { ProjectInventory, InventoryCategory, InventoryType } from '../../../types/inventory';
import realInventoryService from '../../../services/inventoryService';
import Modal from '../../common/Modal';

interface InventoryItemsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const InventoryItemsModal: React.FC<InventoryItemsModalProps> = ({
  isOpen,
  onClose
}) => {
  const [items, setItems] = useState<ProjectInventory[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10; // 5 rows × 2 cards = 10 cards per page

  // Search filters
  const [searchText, setSearchText] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedSubCategory, setSelectedSubCategory] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');

  // Reference data
  const [categories, setCategories] = useState<InventoryCategory[]>([]);
  const [subCategories, setSubCategories] = useState<InventoryType[]>([]);
  const [statuses, setStatuses] = useState<any[]>([]);

  useEffect(() => {
    if (isOpen) {
      loadInitialData();
    }
  }, [isOpen]);

  // Trigger search when any filter changes
  useEffect(() => {
    if (categories.length > 0) { // Only search after reference data is loaded
      performSearch();
    }
  }, [searchText, selectedCategory, selectedSubCategory, selectedStatus, categories]);

  // Calculate pagination
  const totalPages = Math.ceil(items.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = items.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const loadInitialData = async () => {
    try {
      setLoading(true);

      // Load reference data first
      const [categoriesData, statusesData] = await Promise.all([
        realInventoryService.getCategories(),
        realInventoryService.getStatuses()
      ]);

      setCategories(categoriesData);
      setStatuses(statusesData);

      // Initial search with no filters
      await performSearch();

    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSubCategories = async (categoryId: string) => {
    try {
      if (!categoryId) {
        setSubCategories([]);
        return;
      }

      const typesData = await realInventoryService.getTypes({ category_id: parseInt(categoryId) });
      setSubCategories(typesData);
    } catch (error) {
      console.error('Error loading sub-categories:', error);
      setSubCategories([]);
    }
  };

  const performSearch = async () => {
    try {
      setLoading(true);
      setCurrentPage(1); // Reset to first page

      // Build search filters
      const filters: any = {};

      if (searchText.trim()) {
        filters.search = searchText.trim();
      }

      if (selectedCategory) {
        filters.category_id = parseInt(selectedCategory);
      }

      if (selectedSubCategory) {
        filters.type_id = parseInt(selectedSubCategory);
      }

      if (selectedStatus !== 'all') {
        filters.is_active = selectedStatus === 'active';
      }

      // Call backend API with filters
      const results = await realInventoryService.getInventoryItems(filters);
      setItems(results);

    } catch (error) {
      console.error('Error performing search:', error);
    } finally {
      setLoading(false);
    }
  };

  // Event handlers
  const handleSearchTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const categoryId = e.target.value;
    setSelectedCategory(categoryId);
    setSelectedSubCategory(''); // Reset sub-category when category changes

    // Load sub-categories for the selected category
    if (categoryId) {
      loadSubCategories(categoryId);
    } else {
      setSubCategories([]);
    }
  };

  const handleSubCategoryChange = async (event: React.ChangeEvent<HTMLSelectElement>) => {
    const subCategoryValue = event.target.value;
    setSelectedSubCategory(subCategoryValue);
  };

  const handleReset = async () => {
    setSearchText('');
    setSelectedCategory('');
    setSelectedSubCategory('');
    setSelectedStatus('all');
    setCurrentPage(1);
    setSubCategories([]); // Clear sub-categories when category is reset
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedStatus(e.target.value);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <Modal.Header>Inventory Items</Modal.Header>
      <Modal.Body className="!p-0 overflow-hidden">
        <div className="modal-content">
          <div className="list-view">
            {/* Search Controls and Add Button Row */}
            <div className="search-and-action-row">
              <div className="search-controls">
                <div className="search-row">
                  {/* Text Search */}
                  <div className="search-field">
                    <input
                      type="text"
                      placeholder="Search (Name, Code, or Path)..."
                      value={searchText}
                      onChange={handleSearchTextChange}
                      className="search-input"
                      data-testid="INP_inventory_item_search"
                    />
                  </div>

                  {/* Category Filter */}
                  <div className="search-field">
                    <select
                      value={selectedCategory}
                      onChange={handleCategoryChange}
                      className="search-select"
                      data-testid="SEL_inventory_item_category"
                    >
                      <option value="">All Categories</option>
                      {categories.map(category => (
                        <option key={category.id} value={category.id}>
                          {category.category_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Sub-Category Filter */}
                  <div className="search-field">
                    <select
                      value={selectedSubCategory}
                      onChange={handleSubCategoryChange}
                      className="search-select"
                      disabled={!selectedCategory}
                      data-testid="SEL_inventory_item_subcategory"
                    >
                      <option value="">All Sub-Categories</option>
                      {subCategories.map(subCategory => (
                        <option key={subCategory.id} value={subCategory.id}>
                          {subCategory.type_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Status Filter */}
                  <div className="search-field">
                    <select
                      value={selectedStatus}
                      onChange={handleStatusChange}
                      className="search-select"
                      data-testid="SEL_inventory_item_status"
                    >
                      <option value="all">All Status</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>

                  {/* Reset Button */}
                  <div className="search-field reset-button-field">
                    <button
                      className="reset-btn-icon"
                      onClick={handleReset}
                      title="Reset all filters"
                      data-testid="BTN_inventory_item_reset"
                    >
                      ↻
                    </button>
                  </div>
                </div>
              </div>

              {/* Add Item Button - Outside the bordered box but same row */}
              <button className="add-btn-primary add-btn-external" data-testid="BTN_inventory_item_add">
                <span>+</span>
                Add Item
              </button>
            </div>

            {/* Items Display */}
            {loading ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Loading inventory items...</p>
              </div>
            ) : (
              <>
                {items.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-state-content">
                      <div className="empty-state-icon">📦</div>
                      <h3>No inventory items found</h3>
                      <p>No inventory items available to display.</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="grid_2">
                      {currentItems.map((item) => {
                        console.log('Inventory Item:', {
                          id: item.id,
                          inventory_code: item.inventory_code,
                          name: item.name,
                          description: item.description
                        });
                        return (
                      <div key={item.id} className="data_card_2">
                        {/* Header Row: Code + Name */}
                        <div className="data_card_header">
                          <div className="code-name-row">
                            <span className="data_card_header">{item.inventory_code || 'NO-CODE'}</span>
                            <span className="data_card_header" style={{fontWeight: 'bold'}}>{item.name || 'NO-NAME'}</span>
                          </div>
                        </div>

                        {/* Description (full width with wrap, fixed height) */}
                        <div className="data_default">
                          {item.description || 'No description available'}
                        </div>

                        {/* Categories and Status Tags */}
                        <div className="data_tags">
                          <span>Category: {item.category_name || 'N/A'}</span>
                          <span> • </span>
                          <span>Sub: {item.type_name || 'N/A'}</span>
                          <span> • </span>
                          <span>Status: {item.status_name || 'Unknown'}</span>
                        </div>

                        {/* Footer with File Path */}
                        <div className="data_card_footer">
                          <div className="data_paths">
                            📁 {item.full_file_path || item.folder_path || item.business_purpose || 'No path specified'}
                          </div>
                        </div>
                      </div>
                        );
                      })}
                    </div>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                      <div className="pagination">
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="pagination-btn"
                          data-testid="BTN_inventory_item_page_prev"
                        >
                          Previous
                        </button>

                        <div className="pagination-info">
                          Page {currentPage} of {totalPages} ({items.length} total items)
                        </div>

                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="pagination-btn"
                          data-testid="BTN_inventory_item_page_next"
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default InventoryItemsModal;
